import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Calculator, FlaskConical, Globe, BookOpen, Languages,
  MessageCircle, Brain, Trophy, Star, Flame, ArrowRight,
  Camera, AlertTriangle, ClipboardList, CalendarClock,
  Target, PenTool, CheckCircle, FileText, Zap
} from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const iconMap = { Calculator, FlaskConical, Globe, BookOpen, Languages };

const colorMap = {
  "#4F46E5": { bg: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/30", hover: "hover:border-blue-400", gradient: "from-blue-500 to-blue-700" },
  "#10B981": { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/30", hover: "hover:border-emerald-400", gradient: "from-emerald-500 to-teal-600" },
  "#F59E0B": { bg: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/30", hover: "hover:border-orange-400", gradient: "from-orange-500 to-orange-700" },
  "#EC4899": { bg: "bg-rose-500/10", text: "text-rose-400", border: "border-rose-500/30", hover: "hover:border-rose-400", gradient: "from-rose-500 to-pink-600" },
  "#8B5CF6": { bg: "bg-violet-500/10", text: "text-violet-400", border: "border-violet-500/30", hover: "hover:border-violet-400", gradient: "from-violet-500 to-purple-600" },
};

const subCategoryIcons = {
  "History": "from-amber-500 to-orange-600",
  "Geography": "from-green-500 to-emerald-600",
  "Civics": "from-blue-500 to-indigo-600",
  "Political Science": "from-blue-500 to-indigo-600",
  "Economics": "from-violet-500 to-purple-600",
};

const fadeUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };

export default function DashboardPage() {
  const { user, updateGrade, updateBoard, updateStream } = useAuth();
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState(() => {
    try { const c = sessionStorage.getItem("cached_subjects"); return c ? JSON.parse(c) : []; } catch { return []; }
  });
  const [progress, setProgress] = useState(null);
  const [topStudents, setTopStudents] = useState([]);
  const [loading, setLoading] = useState(!sessionStorage.getItem("cached_subjects"));
  const token = localStorage.getItem("token");

  useEffect(() => { fetchData(); }, [user?.grade, user?.board, user?.stream]);

  const fetchData = async () => {
    try {
      const g = user?.grade || 6;
      const b = user?.board || "CBSE";
      const s = user?.stream || "";
      let subUrl = `${API}/subjects?grade=${g}&board=${b}`;
      if (g >= 11 && s) {
        subUrl += `&stream=${s}`;
      }
      const [subRes, progRes, sowRes] = await Promise.all([
        axios.get(subUrl),
        axios.get(`${API}/progress`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/student-of-week?grade=${g}`).catch(() => ({ data: { students: [] } }))
      ]);
      setSubjects(subRes.data);
      sessionStorage.setItem("cached_subjects", JSON.stringify(subRes.data));
      setProgress(progRes.data);
      setTopStudents(sowRes.data?.students || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #0F1B3D 0%, #0D1733 100%)' }}>
        <div className="w-10 h-10 border-4 border-blue-500/30 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0F1B3D 0%, #0D1733 100%)' }} data-testid="dashboard-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Card - SuperTutor inspired */}
        <div className="mb-6">
          <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-blue-600 to-indigo-700 rounded-3xl p-6 text-white shadow-lg">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-8 translate-x-8" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-6 -translate-x-6" />
            <div className="relative">
              <h1 className="font-heading text-2xl sm:text-3xl font-black">
                Hi, {user?.name?.split(" ")[0]}! 👋
              </h1>
              <p className="text-white/70 mt-1 text-sm">Ready to learn something new today?</p>
              <div className="flex items-center gap-3 mt-4 flex-wrap">
                <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-full px-4 py-1.5 text-sm font-bold">
                  <Flame className="w-4 h-4 text-orange-300" /> {progress?.streak || 0} day streak
                </div>
                <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-full px-4 py-1.5 text-sm font-bold">
                  <Star className="w-4 h-4 text-amber-300" /> {progress?.xp || 0} XP
                </div>
              </div>
            </div>
            {/* Selectors */}
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              <select
                data-testid="dashboard-grade-select"
                value={String(user?.grade || 6)}
                onChange={e => updateGrade(parseInt(e.target.value))}
                className="rounded-xl border border-white/30 bg-white/15 text-white font-semibold px-3 py-1.5 text-sm outline-none backdrop-blur-sm"
              >
                {[5,6,7,8,9,10,11,12].map(g => (
                  <option key={g} value={String(g)} className="bg-indigo-700 text-white">Grade {g}</option>
                ))}
              </select>
              <select
                data-testid="dashboard-board-select"
                value={user?.board || "CBSE"}
                onChange={e => updateBoard(e.target.value)}
                className="rounded-xl border border-white/30 bg-white/15 text-white font-semibold px-3 py-1.5 text-sm outline-none backdrop-blur-sm"
              >
                <option value="CBSE" className="bg-indigo-700 text-white">CBSE</option>
                <option value="ICSE" className="bg-indigo-700 text-white">ICSE</option>
              </select>
              {(user?.grade >= 11) && (
                <select
                  data-testid="dashboard-stream-select"
                  value={user?.stream || "Science"}
                  onChange={e => updateStream(e.target.value)}
                  className="rounded-xl border border-amber-300/50 bg-amber-500/20 text-white font-semibold px-3 py-1.5 text-sm outline-none"
                >
                  <option value="Science" className="bg-indigo-700 text-white">Science</option>
                  <option value="Commerce" className="bg-indigo-700 text-white">Commerce</option>
                </select>
              )}
            </div>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-purple-500/15 border border-purple-500/30 rounded-2xl p-4" data-testid="stat-quizzes-card">
            <div className="w-9 h-9 rounded-xl bg-purple-500/20 flex items-center justify-center mb-2">
              <Zap className="w-5 h-5 text-purple-400" />
            </div>
            <p className="font-heading font-black text-2xl text-purple-300">{progress?.tests_taken || 0}</p>
            <p className="text-xs text-white/50 font-semibold">Quizzes</p>
          </div>
          <div className="bg-emerald-500/15 border border-emerald-500/30 rounded-2xl p-4" data-testid="stat-score-card">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-2">
              <Target className="w-5 h-5 text-emerald-400" />
            </div>
            <p className="font-heading font-black text-2xl text-emerald-300">{progress?.avg_score || 0}%</p>
            <p className="text-xs text-white/50 font-semibold">Avg Score</p>
          </div>
          <div className="bg-orange-500/15 border border-orange-500/30 rounded-2xl p-4" data-testid="stat-chapters-card">
            <div className="w-9 h-9 rounded-xl bg-orange-500/20 flex items-center justify-center mb-2">
              <BookOpen className="w-5 h-5 text-orange-400" />
            </div>
            <p className="font-heading font-black text-2xl text-orange-300">{progress?.chapters_studied || 0}</p>
            <p className="text-xs text-white/50 font-semibold">Chapters</p>
          </div>
        </div>

        {/* Top Students of the Week */}
        {topStudents.length > 0 && (
          <div className="mb-6" data-testid="student-of-week-card">
            <div className="relative overflow-hidden bg-gradient-to-r from-amber-500/20 via-orange-500/15 to-rose-500/20 backdrop-blur-sm rounded-2xl p-5 border border-amber-500/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg flex-shrink-0">
                  <Trophy className="w-5 h-5 text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className="font-heading font-extrabold text-amber-300 text-sm uppercase tracking-wide flex items-center gap-2">
                    Top Students of the Week <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                  </h3>
                  <p className="text-[11px] text-white/40">Grade {user?.grade} · This week's top performers</p>
                </div>
              </div>
              <div className="space-y-3">
                {topStudents.map((s, idx) => {
                  const medals = ["🥇", "🥈", "🥉"];
                  const nameColors = ["text-amber-300", "text-slate-300", "text-orange-400"];
                  return (
                    <div key={idx} className={`flex items-center gap-3 ${idx === 0 ? 'bg-amber-500/10 rounded-xl p-3 border border-amber-500/20' : 'px-3 py-2'}`} data-testid={`sow-rank-${idx}`}>
                      <span className="text-xl flex-shrink-0 w-8 text-center">{medals[idx]}</span>
                      <div className="flex-1 min-w-0">
                        <p className={`font-heading font-extrabold ${idx === 0 ? 'text-base' : 'text-sm'} ${nameColors[idx]}`}>{s.name}</p>
                        <p className="text-[10px] text-white/40">{s.board} · {s.xp} XP</p>
                      </div>
                      <div className="flex items-center gap-3 text-right flex-shrink-0">
                        <div><p className="font-heading font-bold text-emerald-400 text-sm">{s.tests_this_week}</p><p className="text-[9px] text-white/30">Tests</p></div>
                        <div><p className="font-heading font-bold text-blue-400 text-sm">{s.activities_this_week}</p><p className="text-[9px] text-white/30">Studied</p></div>
                        <div><p className="font-heading font-bold text-orange-400 text-sm">{s.avg_score}%</p><p className="text-[9px] text-white/30">Score</p></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { icon: MessageCircle, label: "Ask a Doubt", sub: "Chat with AI tutor", path: "/doubt", color: "from-blue-500 to-blue-700" },
            { icon: ClipboardList, label: "Take a Test", sub: "MCQ + Written", path: "/test", color: "from-orange-500 to-orange-700" },
            { icon: Camera, label: "Solve Homework", sub: "Upload a photo", path: "/homework", color: "from-rose-500 to-pink-600" },
            { icon: CalendarClock, label: "Exam Countdown", sub: "Plan & practice", path: "/exam-countdown", color: "from-violet-500 to-purple-700" },
          ].map(a => (
            <button key={a.path} data-testid={`quick-${a.label.toLowerCase().replace(/\s/g, '-')}-btn`}
              onClick={() => navigate(a.path)}
              className={`bg-gradient-to-br ${a.color} text-white rounded-2xl p-4 text-left hover:shadow-xl transition-all group shadow-md`}>
              <a.icon className="w-6 h-6 mb-2" strokeWidth={2} />
              <h3 className="font-heading text-sm font-bold mb-0.5">{a.label}</h3>
              <p className="text-white/60 text-[11px] hidden sm:block">{a.sub}</p>
            </button>
          ))}
        </div>

        {/* Weak Topics */}
        {progress?.weak_topics?.length > 0 && (
          <div className="mb-6">
            <div className="bg-white/5 rounded-2xl p-5 border-2 border-orange-500/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-orange-400" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-white">Topics to Improve</h3>
                  <p className="text-xs text-white/40">Based on your test performance</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {progress.weak_topics.slice(0, 8).map((wt, i) => (
                  <button key={i} data-testid={`weak-topic-${i}`}
                    onClick={() => navigate(`/test`)}
                    className="flex items-center gap-2 bg-orange-500/10 text-orange-300 px-3 py-2 rounded-xl text-sm font-medium hover:bg-orange-500/20 transition-colors border border-orange-500/20">
                    <span>{wt.topic}</span>
                    <span className="text-xs bg-orange-500/30 px-1.5 py-0.5 rounded-full">{wt.wrong_count}x</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Subjects Grid */}
        <div>
          <h2 className="font-heading text-xl font-extrabold text-white mb-4">Your Subjects</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {subjects.map(subject => {
              const colors = colorMap[subject.color] || colorMap["#4F46E5"];
              const IconComp = iconMap[subject.icon] || BookOpen;
              const stats = progress?.subject_stats?.[subject.name] || {};
              return (
                <div key={subject.id}
                  className={`bg-white/5 backdrop-blur-sm rounded-2xl p-5 border ${colors.border} ${colors.hover} cursor-pointer transition-all hover:bg-white/10`}
                  onClick={() => navigate(`/subject/${subject.id}`)}
                  data-testid={`subject-card-${subject.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${colors.gradient} flex items-center justify-center shadow-md`}>
                      <IconComp className="w-6 h-6 text-white" strokeWidth={2} />
                    </div>
                    {stats.tests > 0 && (
                      <div className="text-right">
                        <p className="text-xs text-white/40">Avg Score</p>
                        <p className={`font-heading font-bold ${colors.text}`}>{stats.avg_score}%</p>
                      </div>
                    )}
                  </div>
                  <h3 className="font-heading text-lg font-bold text-white mb-1">{subject.name}</h3>
                  <p className="text-sm text-white/40 mb-3">{subject.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-white/30">Grade {user?.grade || 6}</span>
                    <ArrowRight className={`w-4 h-4 ${colors.text}`} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Platform Highlights */}
        <div className="mt-8 mb-6">
          <h2 className="font-heading text-xl font-extrabold text-white mb-4">What You Can Do</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { icon: BookOpen, title: "NCERT & ICSE Aligned", desc: "Every chapter mapped to your textbook", color: "bg-blue-500/15", text: "text-blue-400" },
              { icon: MessageCircle, title: "Ask Any Doubt", desc: "Chat with AI tutor 24/7", color: "bg-orange-500/15", text: "text-orange-400" },
              { icon: Brain, title: "Smart Tests", desc: "MCQ + written tests with AI grading", color: "bg-emerald-500/15", text: "text-emerald-400" },
              { icon: Camera, title: "Photo Solver", desc: "Snap homework, get solutions", color: "bg-rose-500/15", text: "text-rose-400" },
              { icon: Target, title: "Weak Topic Detection", desc: "AI finds where you need to improve", color: "bg-violet-500/15", text: "text-violet-400" },
              { icon: PenTool, title: "Practice Mode", desc: "Unlimited questions with hints", color: "bg-sky-500/15", text: "text-sky-400" },
            ].map(f => (
              <div key={f.title} className={`rounded-2xl p-4 border border-white/10 ${f.color}`}>
                <f.icon className={`w-5 h-5 ${f.text} mb-2`} strokeWidth={2} />
                <h3 className="font-heading text-xs sm:text-sm font-bold text-white mb-0.5">{f.title}</h3>
                <p className="text-white/40 text-[10px] sm:text-xs">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Curriculum Coverage */}
        <div className="mb-8">
          <div className="bg-white/5 rounded-2xl p-5 sm:p-6 border border-white/10">
            <h3 className="font-heading text-base sm:text-lg font-bold text-white mb-3">Complete Curriculum Coverage</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                "CBSE: 476+ NCERT chapters across all subjects",
                "ICSE: 267+ CISCE chapters with board-specific topics",
                "Grades 11-12: Science & Commerce streams",
                "Tests in Indian exam format (MCQ + Short + Long)",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                  <p className="text-white/60 text-xs sm:text-sm">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
